import { useEffect, useState, useCallback } from "react";
import api, { money, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, ArrowsClockwise, Check, X, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useReturnToClose } from "@/hooks/useReturnToClose";

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

export default function RecurringAccruals({ onPosted, standalone }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(null);
  const [modes, setModes] = useState({});

  const load = useCallback(() => {
    api.get("/accruals/recurring/suggestions").then(({ data }) => setData(data)).catch(() => setData({ suggestions: [], count: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);
  useReturnToClose(!!data && data.count === 0, !!data, standalone);

  if (!data || data.count === 0) {
    if (!standalone) return null;
    return (
      <div className="card-surface p-12 text-center" data-testid="recurring-empty">
        <div className="w-14 h-14 rounded-2xl bg-orange/10 flex items-center justify-center mx-auto mb-4"><Sparkle size={28} weight="fill" className="text-orangeink" /></div>
        <div className="font-heading font-bold text-xl text-navy">No recurring accruals to suggest</div>
        <div className="text-sm text-slate-600 mt-1.5">When a regularly-billed supplier is missing for the month, predicted accruals appear here.</div>
      </div>
    );
  }

  const keyOf = (s) => `${s.contact}|${s.account_code}`;
  const modeOf = (s) => modes[keyOf(s)] || "auto";

  const post = async (s) => {
    setBusy(keyOf(s));
    try {
      const { data: res } = await api.post("/accruals/recurring/post", {
        contact: s.contact, account_code: s.account_code, account_name: s.account_name,
        amount: s.predicted_amount, month: data.month, reversal_mode: modeOf(s),
      });
      toast.success(
        (res.queued_for_approval ? "Accrual queued for approval" : "Accrual posted") +
        ` · reverses ${fmtDate(res.reverses_on)}`
      );
      load();
      onPosted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post accrual");
    } finally { setBusy(null); }
  };

  const dismiss = async (s) => {
    setBusy(keyOf(s));
    try {
      await api.post("/accruals/recurring/dismiss", { contact: s.contact, account_code: s.account_code, month: data.month });
      load();
    } catch { toast.error("Could not dismiss"); } finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-orange/30 rounded-2xl mb-5 shadow-card" data-testid="recurring-accruals-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-lg bg-orange/10 text-orangeink flex items-center justify-center"><Sparkle size={16} weight="fill" /></span>
          <div className="text-left">
            <div className="font-heading font-bold text-navy text-base tracking-tight">Recurring bill accruals</div>
            <div className="text-xs text-navy/70">{data.count} supplier{data.count === 1 ? "" : "s"} regularly billed but missing in {monthLabel(data.month)}</div>
          </div>
        </div>
        {open ? <CaretUp size={16} className="text-navy/65" /> : <CaretDown size={16} className="text-navy/65" />}
      </button>
      {open && (
        <div className="divide-y divide-navy/10">
          {data.suggestions.map((s) => {
            const k = keyOf(s);
            return (
              <div key={k} className="px-5 py-4 flex items-center gap-4 flex-wrap" data-testid={`recurring-suggestion-${k}`}>
                <div className="min-w-[180px] flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-navy">{s.contact}</span>
                    <span className={`text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${s.confidence === "high" ? "bg-positive/10 text-positive border-positive/30" : "bg-warn/10 text-warn border-warn/40"}`}>
                      {s.confidence}
                    </span>
                  </div>
                  <div className="text-xs text-navy/70 mt-0.5">{s.account_code} · {s.account_name}</div>
                  <div className="text-[0.7rem] text-navy/65 mt-1 font-mono">
                    {s.history.map((h) => money(h.amount)).join(" · ")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="overline text-navy/65">Predicted accrual</div>
                  <div className="font-mono font-semibold text-lg text-navy tabular" data-testid={`recurring-amount-${k}`}>{money(s.predicted_amount)}</div>
                </div>
                <div>
                  <div className="overline text-navy/65 mb-1">Reversal</div>
                  <select value={modeOf(s)} onChange={(e) => setModes({ ...modes, [k]: e.target.value })}
                    data-testid={`recurring-reversal-${k}`}
                    className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-navy outline-none focus:border-orange">
                    <option value="auto">Auto-reverse next month</option>
                    <option value="smart">Smart-match to actual bill</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => post(s)} disabled={busy === k} data-testid={`recurring-post-${k}`}
                    className="inline-flex items-center gap-1.5 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange transition-colors disabled:opacity-60">
                    <Check size={15} weight="bold" /> {busy === k ? "Posting…" : "Post accrual"}
                  </button>
                  <button onClick={() => dismiss(s)} disabled={busy === k} data-testid={`recurring-dismiss-${k}`}
                    title="Dismiss for this month"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-navy/70 hover:bg-navy/5 transition-colors">
                    <X size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
